import React, { useState, useEffect, useCallback, useRef } from 'react';
import { citasService } from '../../api/citas';
import { pacientesService } from '../../api/pacientes';
import { tratamientosService } from '../../api/tratamientos';
import MOTIVOS_PODOLOGIA from '../../config/motivos-consulta-podologia';
import MOTIVOS_COSMETOLOGIA from '../../config/motivos-consulta-cosmetologia';
import MOTIVOS_MANICURIA from '../../config/motivos-consulta-manicuria';
import useUIStore from '../../store/uiStore';
import { useAuth } from '../../context/AuthContext';

/**
 * QuickBookingModal — Modal de Cita Rápida por Llamada Telefónica
 * 
 * CI-001: Crea paciente + cita en 1 solo paso.
 * Ideal para adultos mayores que llaman por teléfono.
 * 
 * Props:
 *   onClose: () => void  — Cierra el modal
 */
const QuickBookingModal = ({ onClose }) => {
  // ── Estados del formulario ──
  const [step, setStep] = useState(1); // 1: datos, 2: fecha/hora
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('+56');
  const [motivoId, setMotivoId] = useState('');
  const [fecha, setFecha] = useState('');
  const [hora, setHora] = useState('');
  const [notas, setNotas] = useState('');
  
  // ── Modo "crear nuevo paciente" (FIX-002) ──
  const [crearNuevo, setCrearNuevo] = useState(false);
  
  // ── Estados de UI ──
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  
  // ── Autocomplete ──
  const [pacientesEncontrados, setPacientesEncontrados] = useState([]);
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);
  const [busquedaActiva, setBusquedaActiva] = useState(false);
  const debounceTimer = useRef(null);
  
  // ── Tratamientos desde la API ──
  const [tratamientos, setTratamientos] = useState([]);
  const [horarios, setHorarios] = useState([]);

  // ── Precargar datos si viene de Re-agendar ──
  const { quickBookingPrefill } = useUIStore();

  // ── Determinar motivos según especialidad de la cuenta ──
  const { currentUser } = useAuth();
  const especialidad = currentUser?.cuenta?.especialidad || 'podologia';
  const motivosActivos = (() => {
    if (especialidad === 'cosmetologia') return MOTIVOS_COSMETOLOGIA;
    if (especialidad === 'manicuria') return MOTIVOS_MANICURIA;
    if (especialidad === 'multi') return [...MOTIVOS_PODOLOGIA, ...MOTIVOS_COSMETOLOGIA, ...MOTIVOS_MANICURIA];
    return MOTIVOS_PODOLOGIA;
  })();

  // Evitar que arrastrar selección cierre el modal
  const mousedownOnBackdrop = useRef(false);
  const handleMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      mousedownOnBackdrop.current = true;
    } else {
      mousedownOnBackdrop.current = false;
    }
  };
  const handleMouseUp = (e) => {
    if (e.target === e.currentTarget && mousedownOnBackdrop.current) {
      onClose();
    }
    mousedownOnBackdrop.current = false;
  };

  useEffect(() => {
    if (quickBookingPrefill) {
      setNombre(quickBookingPrefill.paciente_nombre || '');
      setTelefono(quickBookingPrefill.paciente_telefono || '+56');

      // Si tiene paciente_id (reagendar), el nombre queda fijo
      if (quickBookingPrefill.paciente_id) {
        setPacienteSeleccionado({ id: quickBookingPrefill.paciente_id, nombre: quickBookingPrefill.paciente_nombre });
      }

      // Buscar el motivo que coincida con el tratamiento
      if (quickBookingPrefill.tratamiento_id) {
        const motivoMatch = motivosActivos.find(m =>
          m.label.toLowerCase().includes((quickBookingPrefill.tratamiento_nombre || '').toLowerCase().slice(0, 6))
        );
        if (motivoMatch) {
          setMotivoId(motivoMatch.id);
        }
      }
    }
  }, [quickBookingPrefill]);

  // ── Cargar tratamientos al montar ──
  useEffect(() => {
    const cargarTratamientos = async () => {
      try {
        const res = await tratamientosService.getAll();
        if (Array.isArray(res.data)) {
          setTratamientos(res.data);
        }
      } catch (e) {
        console.warn('No se pudieron cargar tratamientos:', e);
      }
    };
    cargarTratamientos();
  }, []);
  
  // ── Cargar horarios cuando cambia fecha ──
  useEffect(() => {
    if (!fecha) return;
    const cargarHorarios = async () => {
      try {
        const res = await citasService.getHorariosDisponibles(fecha);
        if (res.data?.horas_disponibles) {
          setHorarios(res.data.horas_disponibles);
        }
      } catch (e) {
        console.warn('Error cargando horarios:', e);
      }
    };
    cargarHorarios();
  }, [fecha]);
  
  // ── Búsqueda de pacientes con debounce ──
  const buscarPacientes = useCallback(async (query) => {
    if (query.length < 3) {
      setPacientesEncontrados([]);
      setBusquedaActiva(false);
      return;
    }
    setBusquedaActiva(true);
    try {
      const res = await pacientesService.buscar(query);
      setPacientesEncontrados(res.data?.results || []);
    } catch (e) {
      console.warn('Error buscando pacientes:', e);
      setPacientesEncontrados([]);
    } finally {
      setBusquedaActiva(false);
    }
  }, []);
  
  const handleNombreChange = (e) => {
    const val = e.target.value;
    setNombre(val);
    setPacienteSeleccionado(null);
    setCrearNuevo(false);
    
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => buscarPacientes(val), 400);
  };
  
  const seleccionarPaciente = (p) => {
    setPacienteSeleccionado(p);
    setNombre(p.nombre);
    setTelefono(p.telefono);
    setPacientesEncontrados([]);
  };
  
  // ── Manejo de teléfono ──
  const handleTelefonoChange = (e) => {
    let val = e.target.value.replace(/[^+\d]/g, '');
    if (!val.startsWith('+')) val = '+56' + val.replace(/^\+?56?/, '');
    setTelefono(val);
  };
  
  // ── Submit ──
  const handleSubmit = async () => {
    setError('');
    
    // Validaciones
    if (!nombre.trim()) {
      setError('El nombre del paciente es obligatorio.');
      return;
    }
    if (!telefono || telefono.length < 8) {
      setError('Ingrese un teléfono válido.');
      return;
    }
    if (!fecha) {
      setError('Seleccione una fecha.');
      return;
    }
    if (!hora) {
      setError('Seleccione una hora.');
      return;
    }
    
    // Determinar tratamiento_id
    let tratamientoId = null;
    const motivoSel = motivosActivos.find(m => m.id === motivoId);
    
    if (motivoSel && tratamientos.length > 0) {
      // Buscar tratamiento en la DB de la clínica
      const match = tratamientos.find(t =>
        t.nombre === motivoSel.tratamiento ||
        (t.nombre_display && t.nombre_display.toLowerCase().includes(motivoSel.label.toLowerCase().slice(0, 8)))
      );
      if (match) tratamientoId = match.id;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        nombre: nombre.trim(),
        telefono: telefono.trim(),
        fecha,
        hora,
        notas: notas.trim(),
      };
      if (tratamientoId) payload.tratamiento_id = tratamientoId;
      
      // Si el usuario seleccion\u00f3 un paciente del autocomplete, usarlo directamente
      if (pacienteSeleccionado) {
        payload.paciente_id = pacienteSeleccionado.id;
      } else if (crearNuevo) {
        // FIX-002: forzar creaci\u00f3n de paciente nuevo con mismo tel\u00e9fono
        payload.crear_nuevo = true;
      }

      // FIX-006: Si es reagendar, pasar paciente_id para actualizar tel\u00e9fono
      if (quickBookingPrefill?.paciente_id) payload.paciente_id = quickBookingPrefill.paciente_id;
      
      const res = await citasService.createRapida(payload);
      setSuccess(res.data);
    } catch (e) {
      const msg = e.response?.data?.error
        || e.response?.data?.detail
        || 'Error al crear la cita. Intente nuevamente.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };
  
  // ── Fecha mínima (hoy) ──
  const todayStr = new Date().toISOString().split('T')[0];
  
  // ── Render: Éxito ──
  if (success) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
          <div className="text-5xl mb-4">✅</div>
          <h2 className="text-xl font-bold text-[#0D2B28] mb-2">
            {quickBookingPrefill?.es_reagendar ? '🔄 Cita re-agendada' : '📅 Cita agendada'}
          </h2>
          <p className="text-gray-600 mb-1">
            <strong>{success.paciente?.nombre}</strong>
          </p>
          <p className="text-gray-500 text-sm mb-4">
            {success.fecha} a las {success.hora}
            {success.paciente?.es_nuevo && (
              <span className="block mt-1 text-yellow-600">
                🆕 Paciente nuevo creado — complete sus datos en Pacientes
              </span>
            )}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-[#2A9D8F] text-white rounded-lg font-medium hover:bg-[#227f74] transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        
        {/* ── Header ── */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-2xl">📞</span>
            <h2 className="text-lg font-bold text-[#0D2B28]">Nueva Cita por Llamada</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-400"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* ── Steps indicator ── */}
        <div className="flex px-5 pt-4 gap-2">
          <div className={`flex-1 h-1.5 rounded-full ${step >= 1 ? 'bg-[#2A9D8F]' : 'bg-gray-200'}`} />
          <div className={`flex-1 h-1.5 rounded-full ${step >= 2 ? 'bg-[#2A9D8F]' : 'bg-gray-200'}`} />
        </div>
        
        <div className="p-5 space-y-4">
          
          {/* ── Error ── */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
          
          {/* ═══════ STEP 1: Datos del Paciente ═══════ */}
          {step === 1 && (
            <>
              {/* Nombre + autocomplete */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                  Nombre del paciente
                </label>
                {quickBookingPrefill?.paciente_id ? (
                  <div className="w-full px-4 py-2.5 rounded-lg border border-[#2A9D8F]/30 bg-[#F4E1D2]/10 text-gray-800 font-medium">
                    {nombre}
                    <span className="ml-2 text-xs text-[#2A9D8F]">🔁 Re-agendando</span>
                  </div>
                ) : (<>
                <input
                  type="text"
                  value={nombre}
                  onChange={handleNombreChange}
                  placeholder="Escriba el nombre..."
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all"
                  autoFocus
                  required
                />
                
                {/* Resultados de búsqueda */}
                {pacientesEncontrados.length > 0 && !quickBookingPrefill?.paciente_id && (
                  <div className="mt-1 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    {/* FIX-002: Opción crear nuevo paciente */}
                    <button
                      onClick={() => {
                        setCrearNuevo(true);
                        setPacienteSeleccionado(null);
                        setPacientesEncontrados([]);
                      }}
                      className="w-full text-left px-4 py-2.5 bg-[#F0FDF4] hover:bg-[#DCFCE7] border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <div className="font-medium text-sm text-[#16A34A]">🆕 Crear paciente nuevo</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Forzar creación de paciente nuevo aunque el teléfono ya exista
                      </div>
                    </button>
                    {pacientesEncontrados.map(p => (
                      <button
                        key={p.id}
                        onClick={() => seleccionarPaciente(p)}
                        className="w-full text-left px-4 py-2.5 hover:bg-[#F4E1D2]/30 border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-sm text-gray-900">{p.nombre}</div>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                          <span>{p.rut}</span>
                          <span>{p.telefono}</span>
                          {p.ultima_cita && (
                            <span className="text-[#2A9D8F]">
                              Última visita: {p.ultima_cita}
                              {p.ultimo_tratamiento && ` · ${p.ultimo_tratamiento}`}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {busquedaActiva && (
                  <div className="mt-1 text-xs text-gray-400">Buscando...</div>
                )}
              </>)}
              </div>
              
              {/* Paciente seleccionado */}
              {pacienteSeleccionado && (
                <div className="p-3 bg-[#F4E1D2]/20 border border-[#2A9D8F]/20 rounded-lg">
                  <div className="flex items-center gap-2 text-sm">
                    <span>✅</span>
                    <span className="font-medium text-gray-800">{pacienteSeleccionado.nombre}</span>
                    <span className="text-gray-500">— {pacienteSeleccionado.rut}</span>
                  </div>
                  {pacienteSeleccionado.ultima_cita && (
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      Última visita: {pacienteSeleccionado.ultima_cita}
                      {pacienteSeleccionado.ultimo_tratamiento && ` · ${pacienteSeleccionado.ultimo_tratamiento}`}
                    </p>
                  )}
                </div>
              )}

              {/* FIX-002: Indicador de "crear nuevo paciente" */}
              {crearNuevo && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-sm">
                    <span>🆕</span>
                    <span className="font-medium text-green-700">Creando paciente nuevo</span>
                    <button
                      onClick={() => setCrearNuevo(false)}
                      className="ml-auto text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
              
              {/* Teléfono */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={telefono}
                  onChange={handleTelefonoChange}
                  placeholder="+56912345678"
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all"
                  required
                />
              </div>
              
              {/* Motivo */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                  Motivo de consulta
                </label>
                <select
                  value={motivoId}
                  onChange={(e) => setMotivoId(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all bg-white"
                >
                  <option value="">Seleccione un motivo...</option>
                  {motivosActivos.map(m => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Notas */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                  Notas (opcional)
                </label>
                <textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  rows={2}
                  placeholder={
                    quickBookingPrefill?.es_reagendar
                      ? `Escriba motivo del re-agendamiento (ej: cambio de horario de cita anterior: ${quickBookingPrefill.tratamiento_nombre || ''})`
                      : "Ej: Paciente llamó por dolor en uña del pie derecho..."
                  }
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all resize-none"
                />
              </div>
              
              <button
                onClick={() => setStep(2)}
                disabled={!nombre.trim() || !telefono || telefono.length < 8}
                className="w-full py-2.5 bg-[#2A9D8F] text-white rounded-lg font-medium hover:bg-[#227f74] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente — Elegir horario →
              </button>
            </>
          )}
          
          {/* ═══════ STEP 2: Fecha y Hora ═══════ */}
          {step === 2 && (
            <>
              <div className="text-sm text-gray-600 mb-2">
                Paciente: <strong>{nombre}</strong>
                {pacienteSeleccionado && (
                  <span className="text-gray-400"> ({pacienteSeleccionado.rut})</span>
                )}
              </div>
              
              {/* Fecha */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                  Fecha
                </label>
                <input
                  type="date"
                  value={fecha}
                  onChange={(e) => { setFecha(e.target.value); setHora(''); }}
                  min={todayStr}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all"
                  required
                />
              </div>
              
              {/* Hora */}
              {fecha && (
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                    Horario disponible
                  </label>
                  {horarios.length === 0 ? (
                    <p className="text-sm text-gray-400">Cargando horarios...</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {horarios.map(h => (
                        <button
                          key={h}
                          onClick={() => setHora(h)}
                          className={`py-2 px-3 rounded-lg text-sm font-medium border transition-all ${
                            hora === h
                              ? 'bg-[#2A9D8F] text-white border-[#2A9D8F]'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-[#2A9D8F] hover:text-[#2A9D8F]'
                          }`}
                        >
                          {h}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-2.5 rounded-lg font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                >
                  ← Atrás
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !hora}
                  className="flex-1 py-2.5 bg-[#2A9D8F] text-white rounded-lg font-medium hover:bg-[#227f74] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Agendando...
                    </>
                  ) : (
                    '✓ Agendar'
                  )}
                </button>
              </div>
            </>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default QuickBookingModal;
